"use client";

import { Link } from "@/i18n/navigation";
import {
  PhoneIcon,
  ClipboardDocumentCheckIcon,
  CheckCircleIcon,
  GiftIcon,
  ScaleIcon,
  ClockIcon,
  MagnifyingGlassIcon,
  ShieldCheckIcon,
  ArrowRightIcon,
} from "@heroicons/react/24/outline";
import CtaLink from "@/components/ui/CtaLink";
import Contact from "@/components/Contact/Contact";
import ContentImage from "@/components/media/ContentImage";

const PHONE_TEL = "+32493966507";
const PHONE_DISPLAY = "+32 (0)493 96 65 07";

// imageId values aren't translatable, kept separate and zipped back onto
// CONTENT[locale].categories by index (same pattern as homepage PageClient.jsx).
const CATEGORY_IMAGE_IDS = [
  "rachat-meuble-ancien",
  "rachat-bijoux-or",
  "rachat-montres",
  "rachat-argenterie",
  "rachat-tableaux",
  "rachat-collection",
];

const REASSURANCE_ICONS = [CheckCircleIcon, ClockIcon, MagnifyingGlassIcon, ShieldCheckIcon];

const CONTENT = {
  fr: {
    heroBadge: "Rachat d'objets de valeur",
    h1: "Rachat d'objets à Bruxelles avant votre vide maison",
    heroText:
      "Avant tout débarras, nous examinons ce qui a une valeur réelle : mobilier ancien, bijoux, argenterie, tableaux ou collections. Lorsque le rachat est justifié, son montant est déduit directement de votre devis à Bruxelles. Ni obligation, ni pression : vous restez libre d'accepter ou de refuser chaque proposition, objet par objet.",
    ctaEstimate: "Faire estimer mes objets",
    categoriesTitle: "Ce que nous rachetons",
    categoriesText:
      "La liste ci-dessous couvre les catégories d'objets que nous examinons le plus souvent lors d'un vide maison ou d'un débarras à Bruxelles. D'autres pièces peuvent également avoir de la valeur — pendules, luminaires anciens ou instruments de musique par exemple — nous les évaluons au cas par cas.",
    categories: [
      { title: "Mobilier ancien", text: "Meubles de style, secrétaires, commodes, tables et sièges anciens." },
      { title: "Bijoux en or et en argent", text: "Bagues, colliers, bracelets et pièces de joaillerie ancienne ou courante." },
      { title: "Montres de marque", text: "Montres mécaniques ou automatiques, avec ou sans boîte d'origine." },
      { title: "Argenterie et vaisselle", text: "Couverts, plateaux et services en argent, porcelaine et cristal anciens." },
      { title: "Tableaux et objets d'art", text: "Peintures, gravures, sculptures et objets décoratifs anciens." },
      { title: "Livres et collections", text: "Bibliothèques complètes, vinyles, timbres, pièces de monnaie et bibelots." },
    ],
    whenSection: {
      title: "Quand le rachat d'objets a du sens",
      p1Before: "Le rachat prend tout son sens dans le cadre d'une intervention plus large : un ",
      linkVideMaison: "vide maison",
      p1Mid1: " après un décès, un ",
      linkDebarrasMaison: "débarras de maison",
      p1Mid2: " avant une vente, ou la libération d'un ",
      linkAppartement: "appartement",
      p1After:
        " à la fin d'un bail. Les maisons occupées depuis plusieurs décennies recèlent souvent des pièces oubliées : un tiroir de bijoux, une bibliothèque ancienne ou des meubles remisés depuis longtemps.",
      p2Before:
        "Les caves, greniers et garages sont aussi des endroits où l'on retrouve fréquemment des objets qui ont traversé plusieurs générations sans avoir été réévalués. Lors d'un ",
      linkCaveGrenierGarage: "débarras de cave, grenier ou garage",
      p2After: ", nous les examinons au même titre que le reste du bien avant évacuation.",
    },
    deroulementTitle: "Comment se déroule l'estimation",
    steps: [
      { n: "1", title: "Premier contact", text: "Par téléphone ou via le formulaire, en décrivant la situation et, si possible, avec quelques photos." },
      { n: "2", title: "Examen des objets", text: "Sur place lors de la visite, ou à distance dans un premier temps selon la situation." },
      { n: "3", title: "Estimation transparente", text: "Nous vous indiquons ce qui a une valeur réelle et sur quelle base, sans jargon." },
      { n: "4", title: "Déduction sur le devis", text: "Le montant retenu vient directement réduire le prix du débarras ou du vide maison." },
    ],
    prepBoxTitle: "Bien préparer les objets avant notre passage",
    prepItems: [
      "Regroupez si possible les objets de valeur dans une même pièce.",
      "Conservez les certificats, factures ou boîtes d'origine si vous les avez encore.",
      "Évitez de nettoyer ou restaurer un objet ancien avant l'estimation : cela peut altérer sa valeur.",
    ],
    factorsTitle: "Ce qui détermine la valeur d'un objet",
    factorsIntro:
      "Il n'existe pas de barème fixe : la valeur d'un objet dépend de plusieurs facteurs combinés, et deux pièces d'apparence similaire peuvent recevoir des estimations très différentes.",
    factors: [
      { title: "État général", text: "Un objet abîmé, incomplet ou fortement usé perd une part importante de sa valeur, même s'il est ancien." },
      { title: "Authenticité et matière", text: "Un métal précieux, une signature d'artiste ou une provenance identifiable pèsent davantage qu'une simple apparence ancienne." },
      { title: "Demande actuelle", text: "Certains styles ou catégories d'objets se vendent plus facilement que d'autres à un moment donné." },
      { title: "Quantité et cohérence", text: "Un ensemble complet (service, collection) est souvent plus intéressant que des pièces isolées et dépareillées." },
    ],
    factorsOutro:
      "C'est pourquoi nous ne communiquons pas de fourchette de prix générique par catégorie d'objet : chaque estimation se fait au cas par cas, après examen réel de la pièce concernée.",
    noValueBoxTitle: "Ce qui n'a généralement pas de valeur de rachat",
    noValueBoxText:
      "Par honnêteté, certains éléments courants d'une maison ou d'un appartement n'ont en général pas de valeur de revente significative : électroménager ancien ou hors service, mobilier en kit ou mélaminé, vaisselle du quotidien sans marque identifiable, ou linge et vêtements usagés. Cela ne signifie pas qu'ils sont perdus : ils sont pris en charge normalement dans le débarras, avec tri, recyclage ou don selon leur état.",
    triSection: {
      title: "Tri, évacuation et rachat : une seule visite",
      p1: "Le rachat d'objets ne fonctionne pas comme un service séparé : il s'intègre dans le tri que nous réalisons de toute façon lors d'un débarras. Les objets à valeur réelle sont mis de côté et estimés ; le reste suit le circuit habituel — évacuation, recyclage ou don selon l'état — sans étape supplémentaire pour vous.",
      p2Before:
        "Nous ne surévaluons pas un objet pour justifier un devis plus élevé, ni ne sous-évaluons une pièce pour réduire artificiellement le montant du rachat : l'objectif est une estimation qui reflète la réalité du marché au moment de l'intervention. Vous pouvez consulter des exemples de chantiers réalisés sur notre page ",
      linkRealisations: "réalisations",
      p2After: ".",
    },
    reassurance: {
      title: "Une estimation honnête, sans obligation",
      text: "Nous ne rachetons que ce qui a une valeur réelle et vous l'expliquons clairement. Aucun objet n'est retiré sans votre accord, et le rachat n'est jamais une condition pour obtenir un devis de débarras à Bruxelles.",
      items: [
        "Estimation gratuite et sans engagement",
        "Déduction directe sur votre devis",
        "Transparence sur les valeurs estimées",
        "Vous restez décisionnaire objet par objet",
      ],
      outroBefore: "Retrouvez les retours de nos clients sur notre page ",
      linkAvis: "avis",
      outroMid: ", ou consultez notre page ",
      linkTarifs: "tarifs",
      outroAfter: " pour comprendre comment le rachat s'articule avec le prix du débarras.",
    },
    faqTitle: "Questions fréquentes sur le rachat d'objets à Bruxelles",
    faq: [
      {
        q: "Comment estimez-vous la valeur d'un objet ?",
        a: "Nous examinons l'objet en tenant compte de sa nature, de son état, de son ancienneté et de la demande actuelle pour ce type de pièce. Cette estimation se fait en présentiel dans la mesure du possible, ou à partir de photos pour un premier avis. Elle reste indicative tant que l'objet n'a pas été examiné physiquement : deux pièces qui se ressemblent peuvent avoir une valeur très différente selon leur état réel.",
      },
      {
        q: "Dois-je être présent à Bruxelles pour l'estimation ?",
        a: "Ce n'est pas indispensable pour un premier avis : des photos nettes, prises sous plusieurs angles et avec un éclairage correct, permettent souvent de se faire une première idée. Pour confirmer une valeur et procéder au rachat, un examen sur place ou lors du passage pour le débarras reste toutefois nécessaire.",
      },
      {
        q: "Que se passe-t-il si mes objets n'ont pas de valeur marchande ?",
        a: "Nous vous le disons clairement, sans détour : tout ce qui a l'air ancien n'a pas nécessairement une valeur de revente. Dans ce cas, les objets suivent simplement le circuit normal du débarras — évacuation, tri ou don selon leur état — sans que cela n'affecte le reste de l'intervention.",
      },
      {
        q: "Le rachat retarde-t-il le débarras ?",
        a: "Non, l'examen des objets fait partie intégrante de notre visite et s'intègre dans le déroulement normal du chantier. Il n'ajoute pas de passage supplémentaire : nous identifions ce qui peut être racheté au moment même où nous évaluons l'ensemble du bien à débarrasser.",
      },
      {
        q: "Puis-je refuser de céder certains objets ?",
        a: "Oui, entièrement. Le rachat porte uniquement sur ce que vous acceptez de nous céder : il ne s'agit ni d'un tri imposé, ni d'une condition pour obtenir un devis. Vous restez libre de conserver un meuble, un bijou ou toute autre pièce à laquelle vous tenez particulièrement, même si nous lui avons attribué une valeur estimée lors de notre passage.",
      },
      {
        q: "Comment le montant du rachat est-il pris en compte ?",
        a: "Le montant estimé des objets rachetés vient en déduction du devis global de débarras ou de vide maison. Il ne s'agit pas d'un paiement séparé versé de votre côté : c'est un ajustement direct sur le prix final, détaillé dans le devis que nous vous communiquons avant toute intervention, afin que le calcul reste clair et vérifiable.",
      },
    ],
    faqOutroBefore: "Pour aller plus loin, consultez notre article ",
    faqLinkArticle1: "rachat d'objets lors d'un vide maison : comment ça marche",
    faqOutroMid: " ou notre guide pour ",
    faqLinkArticle2: "reconnaître un objet de valeur",
    faqOutroAfter: " avant notre passage.",
    ctaFinalTitle: "Faites estimer vos objets avant le débarras",
    ctaFinalText:
      "Décrivez-nous votre situation par téléphone ou via le formulaire : nous vous indiquerons si le rachat est pertinent et comment il s'intègre à votre devis.",
    ctaFinalQuote: "Demander un devis gratuit",
    ctaFinalZones: "Voir nos zones d'intervention à Bruxelles",
  },
  nl: {
    heroBadge: "Inkoop van waardevolle voorwerpen",
    h1: "Inkoop van voorwerpen in Brussel vóór uw ontruiming",
    heroText:
      "Voor elke ontruiming bekijken wij wat werkelijk waarde heeft: antiek meubilair, juwelen, zilverwerk, schilderijen of verzamelingen. Wanneer de inkoop gerechtvaardigd is, wordt het bedrag rechtstreeks afgetrokken van uw offerte in Brussel. Geen verplichting, geen druk: u blijft vrij om elk voorstel te aanvaarden of te weigeren, voorwerp per voorwerp.",
    ctaEstimate: "Mijn voorwerpen laten schatten",
    categoriesTitle: "Wat wij inkopen",
    categoriesText:
      "Onderstaande lijst omvat de categorieën voorwerpen die wij het vaakst onderzoeken tijdens een ontruiming of huis leegmaken in Brussel. Ook andere stukken kunnen waarde hebben — bijvoorbeeld klokken, oude verlichting of muziekinstrumenten — wij beoordelen ze geval per geval.",
    categories: [
      { title: "Antiek meubilair", text: "Stijlmeubelen, secretaires, commodes, tafels en antieke zetels." },
      { title: "Gouden en zilveren juwelen", text: "Ringen, kettingen, armbanden en oude of hedendaagse juwelenstukken." },
      { title: "Merkhorloges", text: "Mechanische of automatische horloges, met of zonder originele doos." },
      { title: "Zilverwerk en servies", text: "Bestek, dienbladen en serviezen in zilver, porselein en antiek kristal." },
      { title: "Schilderijen en kunstvoorwerpen", text: "Schilderijen, gravures, beeldhouwwerken en antieke decoratieve voorwerpen." },
      { title: "Boeken en verzamelingen", text: "Volledige boekenkasten, vinylplaten, postzegels, munten en snuisterijen." },
    ],
    whenSection: {
      title: "Wanneer de inkoop van voorwerpen zinvol is",
      p1Before: "De inkoop krijgt vooral zijn waarde in het kader van een ruimere interventie: een ",
      linkVideMaison: "huis leegmaken",
      p1Mid1: " na een overlijden, een ",
      linkDebarrasMaison: "ontruiming van een huis",
      p1Mid2: " vóór een verkoop, of het vrijmaken van een ",
      linkAppartement: "appartement",
      p1After:
        " aan het einde van een huurcontract. Woningen die al decennia bewoond zijn, herbergen vaak vergeten stukken: een lade vol juwelen, een oude boekenkast of al lang weggeborgen meubels.",
      p2Before:
        "Kelders, zolders en garages zijn eveneens plaatsen waar we regelmatig voorwerpen aantreffen die verschillende generaties hebben doorstaan zonder opnieuw geschat te zijn. Bij een ",
      linkCaveGrenierGarage: "ontruiming van kelder, zolder of garage",
      p2After: " onderzoeken we ze op dezelfde manier als de rest van het pand vóór de afvoer.",
    },
    deroulementTitle: "Hoe verloopt de schatting",
    steps: [
      { n: "1", title: "Eerste contact", text: "Telefonisch of via het formulier, met een beschrijving van de situatie en, indien mogelijk, enkele foto's." },
      { n: "2", title: "Onderzoek van de voorwerpen", text: "Ter plaatse tijdens het bezoek, of eerst op afstand naargelang de situatie." },
      { n: "3", title: "Transparante schatting", text: "Wij laten u weten wat werkelijk waarde heeft en op welke basis, zonder vakjargon." },
      { n: "4", title: "Aftrek van de offerte", text: "Het weerhouden bedrag verlaagt rechtstreeks de prijs van de ontruiming of het leegmaken van het huis." },
    ],
    prepBoxTitle: "De voorwerpen goed voorbereiden vóór ons bezoek",
    prepItems: [
      "Verzamel de waardevolle voorwerpen indien mogelijk in dezelfde ruimte.",
      "Bewaar certificaten, facturen of originele dozen indien u die nog heeft.",
      "Vermijd het reinigen of restaureren van een antiek voorwerp vóór de schatting: dit kan de waarde beïnvloeden.",
    ],
    factorsTitle: "Wat de waarde van een voorwerp bepaalt",
    factorsIntro:
      "Er bestaat geen vast tarief: de waarde van een voorwerp hangt af van verschillende gecombineerde factoren, en twee schijnbaar gelijkaardige stukken kunnen zeer verschillend geschat worden.",
    factors: [
      { title: "Algemene staat", text: "Een beschadigd, onvolledig of sterk versleten voorwerp verliest een aanzienlijk deel van zijn waarde, ook al is het antiek." },
      { title: "Authenticiteit en materiaal", text: "Een edelmetaal, een kunstenaarssignatuur of een herkenbare herkomst wegen zwaarder door dan een louter antiek uiterlijk." },
      { title: "Actuele vraag", text: "Sommige stijlen of categorieën voorwerpen verkopen op een bepaald moment makkelijker dan andere." },
      { title: "Hoeveelheid en samenhang", text: "Een volledig geheel (servies, verzameling) is vaak interessanter dan losse, niet-bijeenpassende stukken." },
    ],
    factorsOutro:
      "Daarom communiceren wij geen algemene prijsvork per categorie voorwerp: elke schatting gebeurt geval per geval, na echt onderzoek van het betrokken stuk.",
    noValueBoxTitle: "Wat doorgaans geen inkoopwaarde heeft",
    noValueBoxText:
      "Voor de eerlijkheid: bepaalde gangbare elementen van een huis of appartement hebben doorgaans geen significante wederverkoopwaarde: oude of defecte huishoudapparaten, kit- of gemelamineerd meubilair, alledaags servies zonder herkenbaar merk, of gebruikt linnen en kleding. Dat betekent niet dat ze verloren zijn: ze worden gewoon meegenomen in de ontruiming, met sortering, recyclage of schenking naargelang hun staat.",
    triSection: {
      title: "Sortering, afvoer en inkoop: één enkel bezoek",
      p1: "De inkoop van voorwerpen werkt niet als een aparte dienst: hij maakt deel uit van de sortering die wij sowieso uitvoeren tijdens een ontruiming. De voorwerpen met werkelijke waarde worden apart gezet en geschat; de rest volgt het gebruikelijke traject — afvoer, recyclage of schenking naargelang de staat — zonder extra stap voor u.",
      p2Before:
        "Wij overschatten geen voorwerp om een hogere offerte te rechtvaardigen, en onderschatten evenmin een stuk om het inkoopbedrag kunstmatig te verlagen: het doel is een schatting die de marktrealiteit op het moment van de interventie weerspiegelt. U kunt voorbeelden van uitgevoerde werven bekijken op onze pagina ",
      linkRealisations: "realisaties",
      p2After: ".",
    },
    reassurance: {
      title: "Een eerlijke schatting, zonder verplichting",
      text: "Wij kopen alleen in wat werkelijk waarde heeft en leggen dit duidelijk uit. Geen enkel voorwerp wordt verwijderd zonder uw akkoord, en de inkoop is nooit een voorwaarde om een offerte voor een ontruiming in Brussel te verkrijgen.",
      items: [
        "Gratis en vrijblijvende schatting",
        "Rechtstreekse aftrek op uw offerte",
        "Transparantie over de geschatte waarden",
        "U blijft beslissen, voorwerp per voorwerp",
      ],
      outroBefore: "Bekijk de ervaringen van onze klanten op onze pagina ",
      linkAvis: "beoordelingen",
      outroMid: ", of raadpleeg onze pagina ",
      linkTarifs: "tarieven",
      outroAfter: " om te begrijpen hoe de inkoop zich verhoudt tot de prijs van de ontruiming.",
    },
    faqTitle: "Veelgestelde vragen over de inkoop van voorwerpen in Brussel",
    faq: [
      {
        q: "Hoe schat u de waarde van een voorwerp?",
        a: "Wij onderzoeken het voorwerp rekening houdend met zijn aard, staat, ouderdom en de huidige vraag naar dit type stuk. Deze schatting gebeurt indien mogelijk ter plaatse, of aan de hand van foto's voor een eerste advies. Ze blijft indicatief zolang het voorwerp niet fysiek is onderzocht: twee gelijkende stukken kunnen sterk in waarde verschillen naargelang hun werkelijke staat.",
      },
      {
        q: "Moet ik in Brussel aanwezig zijn voor de schatting?",
        a: "Dat is niet noodzakelijk voor een eerste advies: duidelijke foto's, genomen vanuit verschillende hoeken en met correcte belichting, geven vaak al een eerste indruk. Om een waarde te bevestigen en tot inkoop over te gaan, blijft een onderzoek ter plaatse of tijdens het bezoek voor de ontruiming echter noodzakelijk.",
      },
      {
        q: "Wat gebeurt er als mijn voorwerpen geen marktwaarde hebben?",
        a: "Wij zeggen het u duidelijk en zonder omwegen: alles wat er antiek uitziet heeft niet noodzakelijk een wederverkoopwaarde. In dat geval volgen de voorwerpen gewoon het normale traject van de ontruiming — afvoer, sortering of schenking naargelang hun staat — zonder invloed op de rest van de interventie.",
      },
      {
        q: "Vertraagt de inkoop de ontruiming?",
        a: "Nee, het onderzoek van de voorwerpen maakt integraal deel uit van ons bezoek en past binnen het normale verloop van de werf. Er komt geen extra bezoek bij: wij identificeren wat kan worden ingekocht op het moment zelf dat wij het volledige te ontruimen pand beoordelen.",
      },
      {
        q: "Kan ik weigeren bepaalde voorwerpen af te staan?",
        a: "Ja, volledig. De inkoop heeft alleen betrekking op wat u ons wenst af te staan: het is geen opgelegde sortering, noch een voorwaarde om een offerte te krijgen. U blijft vrij om een meubel, een juweel of elk ander stuk waaraan u bijzonder gehecht bent te behouden, ook al hebben wij er tijdens ons bezoek een geschatte waarde aan toegekend.",
      },
      {
        q: "Hoe wordt het inkoopbedrag verrekend?",
        a: "Het geschatte bedrag van de ingekochte voorwerpen wordt in mindering gebracht op de globale offerte voor ontruiming of het leegmaken van het huis. Het gaat niet om een aparte betaling aan uw kant: het is een rechtstreekse aanpassing van de eindprijs, gedetailleerd in de offerte die wij u vóór elke interventie bezorgen, zodat de berekening duidelijk en controleerbaar blijft.",
      },
    ],
    faqOutroBefore: "Voor meer informatie, raadpleeg ons artikel ",
    faqLinkArticle1: "inkoop van voorwerpen tijdens een ontruiming: hoe werkt het",
    faqOutroMid: " of onze gids om ",
    faqLinkArticle2: "een waardevol voorwerp te herkennen",
    faqOutroAfter: " vóór ons bezoek.",
    ctaFinalTitle: "Laat uw voorwerpen schatten vóór de ontruiming",
    ctaFinalText:
      "Beschrijf ons uw situatie telefonisch of via het formulier: wij laten u weten of de inkoop relevant is en hoe deze in uw offerte past.",
    ctaFinalQuote: "Gratis offerte aanvragen",
    ctaFinalZones: "Bekijk onze interventiezones in Brussel",
  },
  en: {
    heroBadge: "Buyback of valuable items",
    h1: "Item buyback in Brussels before your house clearance",
    heroText:
      "Before any clearance, we examine what has real value: antique furniture, jewellery, silverware, paintings or collections. When the buyback is justified, its amount is deducted directly from your quote in Brussels. No obligation, no pressure: you remain free to accept or decline each offer, item by item.",
    ctaEstimate: "Get my items appraised",
    categoriesTitle: "What we buy back",
    categoriesText:
      "The list below covers the categories of items we most often examine during a house clearance in Brussels. Other pieces may also have value — clocks, antique lighting or musical instruments, for example — we assess them on a case-by-case basis.",
    categories: [
      { title: "Antique furniture", text: "Period furniture, writing desks, chests of drawers, tables and antique chairs." },
      { title: "Gold and silver jewellery", text: "Rings, necklaces, bracelets and antique or contemporary jewellery pieces." },
      { title: "Branded watches", text: "Mechanical or automatic watches, with or without their original box." },
      { title: "Silverware and tableware", text: "Cutlery, trays and sets in silver, porcelain and antique crystal." },
      { title: "Paintings and art objects", text: "Paintings, engravings, sculptures and antique decorative objects." },
      { title: "Books and collections", text: "Complete libraries, vinyl records, stamps, coins and collectibles." },
    ],
    whenSection: {
      title: "When item buyback makes sense",
      p1Before: "Buyback makes the most sense as part of a broader intervention: a ",
      linkVideMaison: "house clearance",
      p1Mid1: " after a bereavement, a ",
      linkDebarrasMaison: "house clearance",
      p1Mid2: " before a sale, or vacating an ",
      linkAppartement: "apartment",
      p1After:
        " at the end of a lease. Homes lived in for several decades often hold forgotten pieces: a drawer of jewellery, an old bookcase or furniture stored away long ago.",
      p2Before:
        "Cellars, attics and garages are also places where we frequently find items that have passed through several generations without being reassessed. During a ",
      linkCaveGrenierGarage: "cellar, attic or garage clearance",
      p2After: ", we examine them in the same way as the rest of the property before removal.",
    },
    deroulementTitle: "How the appraisal works",
    steps: [
      { n: "1", title: "First contact", text: "By phone or via the form, describing the situation and, if possible, with a few photos." },
      { n: "2", title: "Examination of the items", text: "On site during the visit, or remotely at first depending on the situation." },
      { n: "3", title: "Transparent appraisal", text: "We tell you what has real value and on what basis, without jargon." },
      { n: "4", title: "Deduction from the quote", text: "The amount retained directly reduces the price of the clearance or house clearance." },
    ],
    prepBoxTitle: "Preparing the items properly before our visit",
    prepItems: [
      "Group the valuable items together in one room if possible.",
      "Keep any certificates, invoices or original boxes you still have.",
      "Avoid cleaning or restoring an antique item before the appraisal: this can affect its value.",
    ],
    factorsTitle: "What determines the value of an item",
    factorsIntro:
      "There is no fixed scale: an item's value depends on several combined factors, and two similar-looking pieces can receive very different appraisals.",
    factors: [
      { title: "General condition", text: "A damaged, incomplete or heavily worn item loses a significant part of its value, even if it is old." },
      { title: "Authenticity and material", text: "A precious metal, an artist's signature or an identifiable provenance carry more weight than a simply antique appearance." },
      { title: "Current demand", text: "Certain styles or categories of items sell more easily than others at any given time." },
      { title: "Quantity and consistency", text: "A complete set (service, collection) is often more valuable than isolated, mismatched pieces." },
    ],
    factorsOutro:
      "That is why we do not communicate a generic price range per item category: each appraisal is done case by case, after a real examination of the piece concerned.",
    noValueBoxTitle: "What generally has no buyback value",
    noValueBoxText:
      "In all honesty, certain common items found in a house or apartment generally have no significant resale value: old or non-working appliances, flat-pack or laminate furniture, everyday tableware with no identifiable brand, or used linen and clothing. This does not mean they are wasted: they are handled normally as part of the clearance, with sorting, recycling or donation depending on their condition.",
    triSection: {
      title: "Sorting, removal and buyback: a single visit",
      p1: "Item buyback does not work as a separate service: it is built into the sorting we carry out anyway during a clearance. Items of real value are set aside and appraised; the rest follows the usual route — removal, recycling or donation depending on condition — with no extra step for you.",
      p2Before:
        "We do not overvalue an item to justify a higher quote, nor do we undervalue a piece to artificially reduce the buyback amount: the goal is an appraisal that reflects the actual market at the time of the intervention. You can see examples of completed jobs on our ",
      linkRealisations: "our work",
      p2After: " page.",
    },
    reassurance: {
      title: "An honest appraisal, with no obligation",
      text: "We only buy back what has real value, and we explain this clearly. No item is removed without your agreement, and buyback is never a condition for obtaining a clearance quote in Brussels.",
      items: [
        "Free, no-obligation appraisal",
        "Direct deduction from your quote",
        "Transparency on estimated values",
        "You remain the decision-maker, item by item",
      ],
      outroBefore: "Read our customers' feedback on our ",
      linkAvis: "reviews",
      outroMid: " page, or check our ",
      linkTarifs: "prices",
      outroAfter: " page to understand how buyback fits into the cost of the clearance.",
    },
    faqTitle: "Frequently asked questions about item buyback in Brussels",
    faq: [
      {
        q: "How do you appraise the value of an item?",
        a: "We examine the item taking into account its nature, condition, age and current demand for this type of piece. This appraisal is done in person whenever possible, or from photos for an initial opinion. It remains indicative until the item has been physically examined: two similar-looking pieces can have very different values depending on their actual condition.",
      },
      {
        q: "Do I need to be present in Brussels for the appraisal?",
        a: "This is not essential for an initial opinion: clear photos, taken from several angles with good lighting, are often enough to form a first impression. To confirm a value and proceed with the buyback, an on-site examination — either separately or during the clearance visit — is still required.",
      },
      {
        q: "What happens if my items have no market value?",
        a: "We tell you clearly and honestly: not everything that looks old necessarily has resale value. In that case, the items simply follow the normal clearance process — removal, sorting or donation depending on their condition — without affecting the rest of the intervention.",
      },
      {
        q: "Does buyback delay the clearance?",
        a: "No, examining the items is fully integrated into our visit and fits into the normal course of the job. It does not add an extra visit: we identify what can be bought back at the same time as we assess the entire property to be cleared.",
      },
      {
        q: "Can I refuse to give up certain items?",
        a: "Yes, entirely. Buyback only applies to what you agree to give us: it is neither an imposed sorting process nor a condition for obtaining a quote. You remain free to keep a piece of furniture, a piece of jewellery or any other item you are particularly attached to, even if we assigned it an estimated value during our visit.",
      },
      {
        q: "How is the buyback amount taken into account?",
        a: "The estimated amount of the items bought back is deducted from the overall clearance or house-clearance quote. It is not a separate payment made to you: it is a direct adjustment to the final price, detailed in the quote we provide before any intervention, so the calculation remains clear and verifiable.",
      },
    ],
    faqOutroBefore: "To learn more, see our article on ",
    faqLinkArticle1: "item buyback during a house clearance: how it works",
    faqOutroMid: " or our guide to ",
    faqLinkArticle2: "recognising a valuable item",
    faqOutroAfter: " before our visit.",
    ctaFinalTitle: "Get your items appraised before the clearance",
    ctaFinalText:
      "Describe your situation to us by phone or via the form: we will let you know if buyback is relevant and how it fits into your quote.",
    ctaFinalQuote: "Request a free quote",
    ctaFinalZones: "See our service areas in Brussels",
  },
};

export default function PageClient({ locale }) {
  const c = CONTENT[locale];

  const faqStructuredData = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: c.faq.map((item) => ({
      "@type": "Question",
      name: item.q,
      acceptedAnswer: { "@type": "Answer", text: item.a },
    })),
  };

  return (
    <div>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqStructuredData) }}
      />

      {/* HERO */}
      <section className="relative overflow-hidden bg-sand text-ink">
        <div
          className="pointer-events-none absolute inset-0"
          style={{
            background:
              "radial-gradient(1000px 480px at 15% -10%, rgba(63,108,81,0.08), transparent 55%)",
          }}
        />
        <div className="relative mx-auto max-w-[1200px] px-6 py-20 lg:px-10 lg:py-28">
          <div className="inline-flex items-center gap-2 rounded-lg border border-sage/30 bg-sage/10 px-4 py-2 text-xs font-bold uppercase tracking-[0.14em] text-sage">
            <GiftIcon className="h-4 w-4" />
            {c.heroBadge}
          </div>

          <h1 className="mt-6 max-w-3xl font-serif text-4xl font-black leading-[1.1] md:text-5xl">
            {c.h1}
          </h1>

          <p className="mt-6 max-w-2xl text-lg text-ink/70 leading-relaxed">
            {c.heroText}
          </p>

          <div className="mt-9 flex flex-wrap gap-4">
            <CtaLink href={`tel:${PHONE_TEL}`} variant="accent" icon={PhoneIcon}>
              {PHONE_DISPLAY}
            </CtaLink>
            <CtaLink href="#contactus" variant="sand" icon={ClipboardDocumentCheckIcon}>
              {c.ctaEstimate}
            </CtaLink>
          </div>
        </div>
      </section>

      {/* CATEGORIES */}
      <section className="bg-white py-20">
        <div className="mx-auto max-w-[1200px] px-6 lg:px-10">
          <h2 className="font-serif text-2xl md:text-3xl font-extrabold text-brand-900">
            {c.categoriesTitle}
          </h2>
          <p className="mt-4 max-w-2xl leading-relaxed text-neutral-700">
            {c.categoriesText}
          </p>

          <div className="mt-10 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {c.categories.map((cat, i) => (
              <div key={cat.title} className="overflow-hidden rounded-3xl border border-sand-line bg-sand">
                <div className="relative h-44 w-full">
                  <ContentImage
                    imageId={CATEGORY_IMAGE_IDS[i]}
                    className="h-full w-full object-cover"
                    sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
                  />
                </div>
                <div className="p-6">
                  <p className="font-serif text-lg font-bold text-ink">{cat.title}</p>
                  <p className="mt-2 text-sm text-neutral-600">{cat.text}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* QUAND LE RACHAT A DU SENS */}
      <section className="bg-sand py-20">
        <div className="mx-auto max-w-[1000px] px-6 lg:px-10">
          <h2 className="font-serif text-2xl md:text-3xl font-extrabold text-brand-900">
            {c.whenSection.title}
          </h2>
          <p className="mt-4 leading-relaxed text-neutral-700">
            {c.whenSection.p1Before}
            <Link href="/vide-maison" className="font-semibold text-sage underline decoration-sage/40 underline-offset-2 hover:text-sage-light">
              {c.whenSection.linkVideMaison}
            </Link>
            {c.whenSection.p1Mid1}
            <Link href="/debarras-maison" className="font-semibold text-sage underline decoration-sage/40 underline-offset-2 hover:text-sage-light">
              {c.whenSection.linkDebarrasMaison}
            </Link>
            {c.whenSection.p1Mid2}
            <Link href="/debarras-appartement" className="font-semibold text-sage underline decoration-sage/40 underline-offset-2 hover:text-sage-light">
              {c.whenSection.linkAppartement}
            </Link>
            {c.whenSection.p1After}
          </p>
          <p className="mt-4 leading-relaxed text-neutral-700">
            {c.whenSection.p2Before}
            <Link href="/debarras-cave-grenier-garage" className="font-semibold text-sage underline decoration-sage/40 underline-offset-2 hover:text-sage-light">
              {c.whenSection.linkCaveGrenierGarage}
            </Link>
            {c.whenSection.p2After}
          </p>
        </div>
      </section>

      {/* DEROULEMENT */}
      <section className="bg-white py-20">
        <div className="mx-auto max-w-[1000px] px-6 lg:px-10">
          <h2 className="font-serif text-2xl md:text-3xl font-extrabold text-brand-900">
            {c.deroulementTitle}
          </h2>

          <ol className="mt-10 space-y-6">
            {c.steps.map((step) => (
              <li key={step.n} className="flex gap-5 rounded-3xl border border-sand-line bg-sand p-6">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-ink font-serif font-bold text-white">
                  {step.n}
                </span>
                <div>
                  <p className="font-bold text-ink">{step.title}</p>
                  <p className="mt-1 text-sm text-neutral-600">{step.text}</p>
                </div>
              </li>
            ))}
          </ol>

          <div className="mt-10 rounded-3xl border border-sand-line bg-sand/60 p-6">
            <p className="font-bold text-ink">{c.prepBoxTitle}</p>
            <ul className="mt-3 space-y-2 text-sm text-neutral-600">
              {c.prepItems.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* FACTEURS DE VALEUR */}
      <section className="bg-sage/5 py-20">
        <div className="mx-auto max-w-[1000px] px-6 lg:px-10">
          <div className="flex items-center gap-3">
            <ScaleIcon className="h-8 w-8 text-sage" />
            <h2 className="font-serif text-2xl md:text-3xl font-extrabold text-brand-900">
              {c.factorsTitle}
            </h2>
          </div>
          <p className="mt-4 leading-relaxed text-neutral-700">
            {c.factorsIntro}
          </p>

          <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2">
            {c.factors.map((factor) => (
              <div key={factor.title} className="rounded-2xl border border-sand-line bg-white p-5">
                <p className="font-bold text-ink">{factor.title}</p>
                <p className="mt-2 text-sm text-neutral-600">{factor.text}</p>
              </div>
            ))}
          </div>

          <p className="mt-6 leading-relaxed text-neutral-700">
            {c.factorsOutro}
          </p>

          <div className="mt-8 rounded-2xl border border-sand-line bg-white p-6">
            <p className="font-bold text-ink">{c.noValueBoxTitle}</p>
            <p className="mt-2 text-sm leading-relaxed text-neutral-600">
              {c.noValueBoxText}
            </p>
          </div>
        </div>
      </section>

      {/* TRI / EVACUATION / METHODE */}
      <section className="bg-white py-20">
        <div className="mx-auto max-w-[1000px] px-6 lg:px-10">
          <h2 className="font-serif text-2xl md:text-3xl font-extrabold text-brand-900">
            {c.triSection.title}
          </h2>
          <p className="mt-4 leading-relaxed text-neutral-700">
            {c.triSection.p1}
          </p>
          <p className="mt-4 leading-relaxed text-neutral-700">
            {c.triSection.p2Before}
            <Link href="/realisations" className="font-semibold text-sage underline decoration-sage/40 underline-offset-2 hover:text-sage-light">
              {c.triSection.linkRealisations}
            </Link>
            {c.triSection.p2After}
          </p>
        </div>
      </section>

      {/* REASSURANCE / POURQUOI NOUS */}
      <section className="bg-sand py-20">
        <div className="mx-auto max-w-[1000px] px-6 lg:px-10">
          <h2 className="text-center font-serif text-2xl md:text-3xl font-extrabold text-brand-900">
            {c.reassurance.title}
          </h2>
          <p className="mx-auto mt-4 max-w-2xl text-center leading-relaxed text-neutral-700">
            {c.reassurance.text}
          </p>

          <div className="mt-8 flex flex-wrap justify-center gap-4">
            {c.reassurance.items.map((item, i) => {
              const Icon = REASSURANCE_ICONS[i];
              return (
                <span key={item} className="inline-flex items-center gap-2 rounded-full border border-sage/30 bg-white px-4 py-2 text-sm font-semibold text-ink">
                  <Icon className="h-4 w-4 text-sage" />
                  {item}
                </span>
              );
            })}
          </div>

          <p className="mx-auto mt-8 max-w-2xl text-center text-sm text-neutral-600">
            {c.reassurance.outroBefore}
            <Link href="/avis" className="font-semibold text-sage underline decoration-sage/40 underline-offset-2 hover:text-sage-light">
              {c.reassurance.linkAvis}
            </Link>
            {c.reassurance.outroMid}
            <Link href="/tarifs" className="font-semibold text-sage underline decoration-sage/40 underline-offset-2 hover:text-sage-light">
              {c.reassurance.linkTarifs}
            </Link>
            {c.reassurance.outroAfter}
          </p>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-white py-20">
        <div className="mx-auto max-w-[1000px] px-6 lg:px-10">
          <h2 className="font-serif text-2xl md:text-3xl font-extrabold text-brand-900">
            {c.faqTitle}
          </h2>
          <div className="mt-8 space-y-4">
            {c.faq.map((item) => (
              <div key={item.q} className="rounded-2xl border border-sand-line bg-sand p-5">
                <p className="font-bold text-ink">{item.q}</p>
                <p className="mt-2 text-sm leading-relaxed text-neutral-600">{item.a}</p>
              </div>
            ))}
          </div>

          <p className="mt-8 text-sm text-neutral-600">
            {c.faqOutroBefore}
            <Link href="/blog/rachat-objets-vide-maison" className="font-semibold text-sage underline decoration-sage/40 underline-offset-2 hover:text-sage-light">
              {c.faqLinkArticle1}
            </Link>
            {c.faqOutroMid}
            <Link href="/blog/reconnaitre-objet-valeur-rachat" className="font-semibold text-sage underline decoration-sage/40 underline-offset-2 hover:text-sage-light">
              {c.faqLinkArticle2}
            </Link>
            {c.faqOutroAfter}
          </p>
        </div>
      </section>

      {/* CTA FINAL */}
      <section className="bg-sage/5 py-16">
        <div className="mx-auto max-w-[1000px] px-6 text-center lg:px-10">
          <h2 className="font-serif text-2xl md:text-3xl font-extrabold text-brand-900">
            {c.ctaFinalTitle}
          </h2>
          <p className="mx-auto mt-4 max-w-xl leading-relaxed text-neutral-700">
            {c.ctaFinalText}
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-4">
            <CtaLink href={`tel:${PHONE_TEL}`} variant="accent" icon={PhoneIcon}>
              {PHONE_DISPLAY}
            </CtaLink>
            <CtaLink href="#contactus" variant="sand" icon={ClipboardDocumentCheckIcon}>
              {c.ctaFinalQuote}
            </CtaLink>
          </div>
          <div className="mt-8">
            <Link href="/vide-maison" className="inline-flex items-center gap-2 font-bold text-accent hover:text-accent-dark">
              {c.ctaFinalZones}
              <ArrowRightIcon className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      <Contact />
    </div>
  );
}
